import { useEffect, useState } from 'react'
import { Megaphone, Plus, Send, Trash2, Power } from 'lucide-react'
import { PageHead } from '../AdminLayout'
import { Panel, DataTable, RefreshButton, ConfirmButton, Drawer, Tag } from '../ui'
import { get, post, patch, del } from '../../lib/api'
import { toast } from '../../store'
import { clsx, fa, faDate } from '../../lib/format'

const KINDS: [string, string][] = [
  ['info', 'اطلاع‌رسانی'], ['success', 'موفقیت'], ['warning', 'هشدار'], ['danger', 'مهم'],
]

export default function Announcements() {
  const [rows, setRows] = useState<any[] | null>(null)
  const [edit, setEdit] = useState<any>(null)
  const [broadcast, setBroadcast] = useState(false)
  const [loading, setLoading] = useState(false)

  const load = () => {
    setLoading(true)
    get('/admin/system/announcements').then(setRows).catch(() => setRows([])).finally(() => setLoading(false))
  }
  useEffect(load, [])

  const save = async () => {
    const body = {
      title: edit.title, body: edit.body, kind: edit.kind,
      placement: edit.placement || 'banner', active: edit.active ?? true,
    }
    try {
      if (edit.id) await patch(`/admin/system/announcements/${edit.id}`, body)
      else await post('/admin/system/announcements', body)
      toast.ok('ذخیره شد'); setEdit(null); load()
    } catch (e: any) { toast.err(e.message) }
  }

  return (
    <>
      <PageHead title="اطلاعیه و پیام همگانی" subtitle="نمایش بنر روی سایت یا ارسال پیام به صندوق کاربران"
        action={
          <div className="flex gap-2">
            <RefreshButton onClick={load} loading={loading} />
            <button onClick={() => setBroadcast(true)} className="btn-ghost !py-2 text-xs">
              <Send className="h-3.5 w-3.5" /> پیام همگانی
            </button>
            <button onClick={() => setEdit({ title: '', body: '', kind: 'info', placement: 'banner', active: true })}
              className="btn-primary !py-2 text-xs">
              <Plus className="h-3.5 w-3.5" /> اطلاعیه جدید
            </button>
          </div>
        } />

      <Panel dense>
        <DataTable rows={rows} loading={loading} minWidth={720} empty="اطلاعیه‌ای ثبت نشده است"
          onRowClick={(r: any) => setEdit({ ...r })}
          columns={[
            { key: 'id', header: '#', cell: (r: any) => <span className="num text-slate-500">{fa(r.id)}</span> },
            { key: 't', header: 'عنوان', cell: (r: any) => <span className="text-slate-100">{r.title}</span> },
            {
              key: 'k', header: 'نوع', cell: (r: any) => (
                <Tag kind={r.kind === 'danger' ? 'down' : r.kind === 'success' ? 'up' : r.kind === 'warning' ? 'gold' : 'info'}>
                  {KINDS.find(([v]) => v === r.kind)?.[1] ?? r.kind}
                </Tag>
              ),
            },
            { key: 'p', header: 'محل نمایش', cell: (r: any) => <span className="text-slate-400">{r.placement === 'modal' ? 'پنجره' : 'بنر'}</span> },
            { key: 'a', header: 'وضعیت', cell: (r: any) => r.active ? <Tag kind="up">فعال</Tag> : <Tag kind="muted">غیرفعال</Tag> },
            { key: 'd', header: 'تاریخ', cell: (r: any) => <span className="text-slate-500">{faDate(r.createdAt, false)}</span> },
            {
              key: 'x', header: '', cell: (r: any) => (
                <div className="flex gap-1.5" onClick={(e) => e.stopPropagation()}>
                  <button onClick={async () => {
                    try {
                      await patch(`/admin/system/announcements/${r.id}`, { ...r, active: !r.active })
                      toast.ok('به‌روزرسانی شد'); load()
                    } catch (e: any) { toast.err(e.message) }
                  }} className="btn-ghost !px-2 !py-1.5 text-[11px]">
                    <Power className="h-3 w-3" />
                  </button>
                  <ConfirmButton variant="ghost" label="حذف" question="حذف؟"
                    icon={<Trash2 className="h-3.5 w-3.5" />}
                    onConfirm={async () => {
                      try { await del(`/admin/system/announcements/${r.id}`); toast.ok('حذف شد'); load() }
                      catch (e: any) { toast.err(e.message) }
                    }} />
                </div>
              ),
            },
          ]} />
      </Panel>

      <Drawer open={!!edit} onClose={() => setEdit(null)} title={edit?.id ? 'ویرایش اطلاعیه' : 'اطلاعیه جدید'}>
        {edit && (
          <>
            <label className="label">عنوان</label>
            <input className="input mb-3" value={edit.title} onChange={(e) => setEdit({ ...edit, title: e.target.value })} />
            <label className="label">متن</label>
            <textarea className="input mb-3 min-h-[110px]" value={edit.body}
              onChange={(e) => setEdit({ ...edit, body: e.target.value })} />
            <label className="label">نوع</label>
            <div className="mb-3 flex flex-wrap gap-2">
              {KINDS.map(([v, l]) => (
                <button key={v} onClick={() => setEdit({ ...edit, kind: v })}
                  className={clsx('chip', edit.kind === v && 'border-brand-500/50 bg-brand-500/15 text-brand-200')}>{l}</button>
              ))}
            </div>
            <label className="label">محل نمایش</label>
            <div className="mb-3 flex gap-2">
              {[['banner', 'بنر بالای صفحه'], ['modal', 'پنجره بازشو']].map(([v, l]) => (
                <button key={v} onClick={() => setEdit({ ...edit, placement: v })}
                  className={clsx('chip', edit.placement === v && 'border-brand-500/50 bg-brand-500/15 text-brand-200')}>{l}</button>
              ))}
            </div>
            <label className="mb-4 flex cursor-pointer items-center gap-2 text-xs text-slate-400">
              <input type="checkbox" className="h-4 w-4 accent-brand-500" checked={edit.active}
                onChange={(e) => setEdit({ ...edit, active: e.target.checked })} />
              نمایش فعال باشد
            </label>
            <button onClick={save} disabled={!edit.title} className="btn-primary w-full !py-3">
              <Megaphone className="h-4 w-4" /> ذخیره اطلاعیه
            </button>
          </>
        )}
      </Drawer>

      <BroadcastDrawer open={broadcast} onClose={() => setBroadcast(false)} />
    </>
  )
}

function BroadcastDrawer({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [f, setF] = useState({ title: '', body: '', kind: 'info', audience: 'all', link: '' })
  const [sending, setSending] = useState(false)

  const send = async () => {
    setSending(true)
    try {
      const r: any = await post('/admin/system/broadcast', f)
      toast.ok(`پیام برای ${fa(r.sent)} کاربر ارسال شد`)
      setF({ ...f, title: '', body: '' }); onClose()
    } catch (e: any) { toast.err(e.message) } finally { setSending(false) }
  }

  return (
    <Drawer open={open} onClose={onClose} title="ارسال پیام همگانی">
      <div className="mb-4 rounded-xl border border-gold/25 bg-gold/[.07] p-3 text-[11px] leading-6 text-gold">
        این پیام در صندوق اعلان همه کاربرانِ گروه انتخابی قرار می‌گیرد و قابل بازگشت نیست.
      </div>
      <label className="label">گروه مخاطب</label>
      <select className="input mb-3" value={f.audience} onChange={(e) => setF({ ...f, audience: e.target.value })}>
        {[['all', 'همه کاربران'], ['active', 'کاربران فعال'], ['kyc', 'کاربران احرازشده'], ['vip', 'کاربران VIP']]
          .map(([v, l]) => <option key={v} value={v} className="bg-ink-900">{l}</option>)}
      </select>
      <label className="label">عنوان</label>
      <input className="input mb-3" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} />
      <label className="label">متن</label>
      <textarea className="input mb-3 min-h-[110px]" value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} />
      <label className="label">لینک (اختیاری)</label>
      <input className="input num mb-4" dir="ltr" placeholder="/markets" value={f.link}
        onChange={(e) => setF({ ...f, link: e.target.value })} />
      <button onClick={send} disabled={!f.title || sending} className="btn-primary w-full !py-3">
        <Send className="h-4 w-4" /> ارسال پیام
      </button>
    </Drawer>
  )
}
